import React, { useCallback, useEffect, useMemo, useState } from 'react'
import uniqueId from 'lodash/uniqueId'
import { getTypeName } from '@rawgraphs/rawgraphs-core'
import { getAggregationList } from '../../services/aggregationConfig'
import { tealifyIcon } from '../ChartSelector'
import S from './MobileMappingPanel.module.scss'

const TYPE_META = {
  number: { symbol: '#', cls: S.tagNum },
  string: { symbol: 'Aa', cls: S.tagTxt },
  date:   { symbol: '◷', cls: S.tagDate },
}

function getHint(dimension) {
  if (!dimension.validTypes?.length) return 'Accepts any field type.'
  const labels = { number: 'numeric', string: 'text', date: 'date' }
  const types = dimension.validTypes.map((t) => labels[t] || t).join(' or ')
  return `Accepts ${types} fields.${dimension.multiple ? ' Multiple allowed.' : ''}`
}

function getAssigned(mapping, dimensionId) {
  return mapping[dimensionId]?.value || []
}

function buildRoleMapping(fieldNames, dimension, dataTypes) {
  if (!fieldNames.length) {
    return { ids: [], value: [], isValid: true, mappedType: null }
  }
  const firstType = getTypeName(dataTypes[fieldNames[0]])
  const isValid =
    !dimension.validTypes?.length || dimension.validTypes.includes(firstType)
  return {
    ids: fieldNames.map(() => uniqueId()),
    value: [...fieldNames],
    isValid,
    mappedType: firstType,
  }
}

function removeFieldByName(dimensionMapping, fieldName) {
  const idx = (dimensionMapping.value || []).indexOf(fieldName)
  if (idx === -1) return dimensionMapping
  const nextValue = dimensionMapping.value.filter((_, j) => j !== idx)
  const nextIds = (dimensionMapping.ids || []).filter((_, j) => j !== idx)
  let nextConfig
  if (dimensionMapping.config?.aggregation) {
    nextConfig = {
      aggregation: getAggregationList(dimensionMapping).filter((_, j) => j !== idx),
    }
  }
  return { ...dimensionMapping, value: nextValue, ids: nextIds, config: nextConfig }
}

function nextUnfilledRequired(dimensions, mapping) {
  return (
    dimensions.find((d) => d.required && !getAssigned(mapping, d.id).length) || null
  )
}

function autoMap(dimensions, allFields, currentMapping, dataTypes) {
  const usedFields = new Set(
    Object.values(currentMapping).flatMap((m) => m?.value || [])
  )
  const next = { ...currentMapping }
  for (const dim of dimensions) {
    if (!dim.required) continue
    if (next[dim.id]?.value?.length) continue
    const pick = allFields.find(
      (f) =>
        !usedFields.has(f.name) &&
        (!dim.validTypes?.length || dim.validTypes.includes(f.type))
    )
    if (!pick) continue
    usedFields.add(pick.name)
    next[dim.id] = buildRoleMapping([pick.name], dim, dataTypes)
  }
  return next
}

function StatusDot({ done, required }) {
  if (done) {
    return (
      <span className={`${S.dot} ${S.dotDone}`}>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5">
          <path d="M5 12l5 5L20 6" />
        </svg>
      </span>
    )
  }
  if (required) return <span className={`${S.dot} ${S.dotNeed}`} />
  return <span className={`${S.dot} ${S.dotOpt}`} />
}

function TypeTag({ type }) {
  const meta = TYPE_META[type] || TYPE_META.string
  return <span className={`${S.typeTag} ${meta.cls}`}>{meta.symbol}</span>
}

export default function MobileMappingPanel({
  dimensions,
  dataTypes,
  mapping,
  setMapping,
  currentChart,
  onSwitchToChart,
  pickerDimension,
  onOpenPicker,
  onClosePicker,
  bleedToEdge = true,
  inSheet = true,
}) {
  const [expandedId, setExpandedId] = useState(() => {
    const first = dimensions.find(
      (d) => d.required && !getAssigned(mapping, d.id).length
    )
    return first?.id || dimensions[0]?.id || null
  })

  const [pickerSel, setPickerSel] = useState([])
  const [searchQ, setSearchQ] = useState('')

  // Reset picker selection when a new picker opens
  useEffect(() => {
    if (pickerDimension) {
      setPickerSel(getAssigned(mapping, pickerDimension.id))
      setSearchQ('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerDimension?.id])

  const allFields = useMemo(
    () =>
      Object.entries(dataTypes).map(([name, type]) => ({
        name,
        type: getTypeName(type),
      })),
    [dataTypes]
  )

  const compatibleFields = useMemo(() => {
    if (!pickerDimension) return []
    const q = searchQ.toLowerCase()
    return allFields
      .filter(
        (f) =>
          (!pickerDimension.validTypes?.length ||
            pickerDimension.validTypes.includes(f.type)) &&
          (!q || f.name.toLowerCase().includes(q))
      )
  }, [pickerDimension, allFields, searchQ])

  const requiredDims = dimensions.filter((d) => d.required)
  const mappedRequiredCount = requiredDims.filter(
    (d) => getAssigned(mapping, d.id).length > 0
  ).length

  const handleRemoveField = useCallback(
    (dimensionId, fieldName) => {
      setMapping((prev) => ({
        ...prev,
        [dimensionId]: removeFieldByName(prev[dimensionId] || {}, fieldName),
      }))
    },
    [setMapping]
  )

  const commitPicker = useCallback(() => {
    if (!pickerDimension) return
    const next = buildRoleMapping(pickerSel, pickerDimension, dataTypes)
    setMapping((prev) => {
      const nextMapping = { ...prev, [pickerDimension.id]: next }
      const nextDim = nextUnfilledRequired(dimensions, nextMapping)
      setExpandedId(nextDim?.id || null)
      return nextMapping
    })
    onClosePicker()
  }, [pickerDimension, pickerSel, dataTypes, dimensions, setMapping, onClosePicker])

  const handleSingleFieldSelect = useCallback(
    (fieldName) => {
      if (!pickerDimension) return
      const next = buildRoleMapping([fieldName], pickerDimension, dataTypes)
      setMapping((prev) => {
        const nextMapping = { ...prev, [pickerDimension.id]: next }
        const nextDim = nextUnfilledRequired(dimensions, nextMapping)
        setExpandedId(nextDim?.id || null)
        return nextMapping
      })
      onClosePicker()
    },
    [pickerDimension, dataTypes, dimensions, setMapping, onClosePicker]
  )

  const handleAutoMap = useCallback(() => {
    const next = autoMap(dimensions, allFields, mapping, dataTypes)
    setMapping(next)
    setExpandedId(null)
  }, [dimensions, allFields, mapping, dataTypes, setMapping])

  const handleToggleExpand = useCallback((id) => {
    setExpandedId((prev) => (prev === id ? null : id))
  }, [])

  return (
    <div className={[S.root, bleedToEdge && S.rootBleed].filter(Boolean).join(' ')}>
      <div
        className={[S.track, pickerDimension ? S.trackPushed : '']
          .filter(Boolean)
          .join(' ')}
      >
        {/* ── Pane 1: Roles list ─────────────────────────────────────────── */}
        <div className={S.pane}>
          {/* Selected chart */}
          <div className={[S.chartCard, !inSheet && S.chartCardFlat].filter(Boolean).join(' ')}>
            <span
              className={S.chartCardIcon}
              style={
                currentChart?.metadata?.icon
                  ? {
                      backgroundImage: `url("${tealifyIcon(currentChart.metadata.icon)}")`,
                    }
                  : undefined
              }
            />
            <span className={S.chartCardInfo}>
              <span className={S.chartCardEyebrow}>Selected chart</span>
              <strong className={S.chartCardName}>
                {currentChart?.metadata?.name}
              </strong>
            </span>
            <button
              type="button"
              className={S.chartCardSwitch}
              onClick={onSwitchToChart}
            >
              Switch
            </button>
          </div>

          {/* Status bar */}
          <div className={S.statusBar}>
            <span className={S.progress}>
              <b>
                {mappedRequiredCount} of {requiredDims.length}
              </b>{' '}
              required mapped
            </span>
            <button
              type="button"
              className={S.autoMapBtn}
              onClick={handleAutoMap}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M5 3v4M3 5h4M13 3l2.5 6.5L22 12l-6.5 2.5L13 21l-2.5-6.5L4 12l6.5-2.5z" />
              </svg>
              Auto-map
            </button>
          </div>

          {/* Role accordion */}
          <div className={[S.roleList, !inSheet && S.roleListFlat].filter(Boolean).join(' ')}>
            {dimensions.map((dim) => {
              const assigned = getAssigned(mapping, dim.id)
              const isExpanded = expandedId === dim.id
              const isDone = assigned.length > 0

              return (
                <div key={dim.id} className={S.roleItem}>
                  {!isExpanded ? (
                    <button
                      type="button"
                      className={S.roleRow}
                      onClick={() => handleToggleExpand(dim.id)}
                    >
                      <StatusDot done={isDone} required={dim.required} />
                      <span className={S.roleName}>{dim.name}</span>
                      <span
                        className={[
                          S.roleSummary,
                          !isDone && S.roleSummaryMuted,
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {isDone
                          ? assigned.join(', ')
                          : dim.required
                          ? 'Required — tap to map'
                          : 'Optional — tap to add'}
                      </span>
                      <svg
                        className={S.chevron}
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M9 6l6 6-6 6" />
                      </svg>
                    </button>
                  ) : (
                    <div
                      className={[
                        S.roleCard,
                        dim.required && !isDone ? S.roleCardNeed : '',
                        isDone ? S.roleCardDone : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <button
                        type="button"
                        className={S.roleCardHead}
                        onClick={() => handleToggleExpand(dim.id)}
                      >
                        <span className={S.roleName}>{dim.name}</span>
                        <span
                          className={`${S.pill} ${
                            dim.required ? S.pillReq : S.pillOpt
                          }`}
                        >
                          {dim.required ? 'Required' : 'Optional'}
                        </span>
                        <svg
                          className={S.up}
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M6 15l6-6 6 6" />
                        </svg>
                      </button>

                      <p className={S.roleHint}>{getHint(dim)}</p>

                      {isDone ? (
                        <div className={S.chips}>
                          {assigned.map((fieldName) => {
                            const ft = getTypeName(dataTypes[fieldName])
                            return (
                              <span key={fieldName} className={S.chip}>
                                <TypeTag type={ft} />
                                {fieldName}
                                <button
                                  type="button"
                                  className={S.chipRemove}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleRemoveField(dim.id, fieldName)
                                  }}
                                  aria-label={`Remove ${fieldName}`}
                                >
                                  ×
                                </button>
                              </span>
                            )
                          })}
                        </div>
                      ) : (
                        <p className={S.empty}>
                          No {dim.multiple ? 'fields' : 'field'} yet
                        </p>
                      )}

                      <button
                        type="button"
                        className={S.addBtn}
                        onClick={() => onOpenPicker(dim)}
                      >
                        +{' '}
                        {isDone
                          ? `Edit field${dim.multiple ? 's' : ''}`
                          : `Add field${dim.multiple ? 's' : ''}`}
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* ── Pane 2: Field picker ───────────────────────────────────────── */}
        <div className={S.pane} aria-hidden={!pickerDimension || undefined}>
          {!inSheet && (
            <div className={S.pickerPaneHeader}>
              <button
                type="button"
                className={S.pickerBack}
                onClick={onClosePicker}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 6l-6 6 6 6" />
                </svg>
                Back
              </button>
              <span className={S.pickerPaneTitle}>
                {pickerDimension?.name}
              </span>
            </div>
          )}
          <p className={S.pickerSub}>
            {pickerDimension?.multiple
              ? 'Select any compatible fields'
              : 'Choose one field'}
          </p>

          <div className={S.searchRow}>
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#94A3B8"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M21 21l-4-4" />
            </svg>
            <input
              className={S.searchInput}
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search fields"
            />
          </div>

          <div className={S.pickerList}>
            {compatibleFields.length === 0 ? (
              <p className={S.empty}>No matching fields.</p>
            ) : (
              compatibleFields.map((f) => {
                const isSelected = pickerSel.includes(f.name)
                return (
                  <button
                    key={f.name}
                    type="button"
                    className={[S.pickerRow, isSelected ? S.pickerRowSel : '']
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => {
                      if (!pickerDimension?.multiple) {
                        handleSingleFieldSelect(f.name)
                      } else {
                        setPickerSel((prev) =>
                          prev.includes(f.name)
                            ? prev.filter((n) => n !== f.name)
                            : [...prev, f.name]
                        )
                      }
                    }}
                  >
                    <span
                      className={[
                        S.pickerCheck,
                        !pickerDimension?.multiple && S.pickerCheckRadio,
                        isSelected && S.pickerCheckSel,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {isSelected && '✓'}
                    </span>
                    <TypeTag type={f.type} />
                    <span className={S.pickerFieldName}>{f.name}</span>
                  </button>
                )
              })
            )}
          </div>

          {pickerDimension?.multiple && (
            <div className={S.pickerFooter}>
              <button
                type="button"
                className={[
                  S.pickerAddBtn,
                  !pickerSel.length ? S.pickerAddBtnOff : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={commitPicker}
                disabled={!pickerSel.length}
              >
                Add {pickerSel.length} field
                {pickerSel.length !== 1 ? 's' : ''}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
